import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import servePositionService from '../../services/servePositionService';
import memberService from '../../services/memberService';
import ManagePositionsModal from './ManagePositionsModal';
import { addNotification } from '../../store/slices/uiSlice';
import useExcelBatch, { ExcelBatchButtons, ExcelImportConfirmModal } from '../../hooks/useExcelBatch';
import './ServantList.css';

// ── Helpers ───────────────────────────────────────────────────────
function groupByMember(assignments) {
  const map = new Map();
  for (const a of assignments) {
    if (!map.has(a.member_id)) {
      map.set(a.member_id, {
        id: a.member_id,
        chi_name: a.member_chi_name,
        worship_id: a.member_worship_id,
        mobile: a.member_mobile,
        groups: a.member_groups,
        positions: [],
      });
    }
    map.get(a.member_id).positions.push(a);
  }
  return Array.from(map.values());
}

// ── Add Assignment Modal ──────────────────────────────────────────
const AddAssignmentModal = ({ positions, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [memberQuery, setMemberQuery] = useState('');
  const [memberSuggestions, setMemberSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [positionId, setPositionId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const blurTimerRef = useRef(null);

  const handleMemberSearch = (value) => {
    setMemberQuery(value);
    setSelectedMember(null);
    clearTimeout(debounceRef.current);
    if (value.trim().length < 1) { setMemberSuggestions([]); setShowSuggestions(false); return; }
    setSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const results = await memberService.searchMembers(value.trim(), 10);
        setMemberSuggestions(results || []);
        setShowSuggestions(true);
      } catch { setMemberSuggestions([]); }
      finally { setSearching(false); }
    }, 300);
  };

  const handleSelectMember = (m) => {
    setSelectedMember(m);
    setMemberQuery(`${m.chi_name}${m.worship_id ? ` (${m.worship_id})` : ''}`);
    setMemberSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMember) { dispatch(addNotification({ type: 'error', message: '請選擇會員' })); return; }
    if (!positionId) { dispatch(addNotification({ type: 'error', message: '請選擇崗位' })); return; }
    setSaving(true);
    try {
      await servePositionService.createAssignment({
        member_id: selectedMember.id,
        position_id: parseInt(positionId),
        notes: notes || null,
        status: 'active',
      });
      dispatch(addNotification({ type: 'success', message: '新增事奉分配成功' }));
      onSuccess();
      onClose();
    } catch { dispatch(addNotification({ type: 'error', message: '新增失敗，請重試' })); }
    finally { setSaving(false); }
  };

  return (
    <div className="sl-modal-overlay" onClick={onClose}>
      <div className="sl-modal" onClick={e => e.stopPropagation()}>
        <div className="sl-modal-header">
          <h3>新增事奉分配</h3>
          <button className="sl-modal-close" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="sl-modal-body">
            <div className="sl-form-group">
              <label className="sl-label">會員 <span className="sl-required">*</span></label>
              <div className="sl-autocomplete">
                <input
                  type="text" className="sl-input"
                  placeholder="輸入姓名或崇拜編碼搜尋..."
                  value={memberQuery}
                  onChange={e => handleMemberSearch(e.target.value)}
                  onFocus={() => { clearTimeout(blurTimerRef.current); if (memberSuggestions.length > 0) setShowSuggestions(true); }}
                  onBlur={() => { blurTimerRef.current = setTimeout(() => setShowSuggestions(false), 200); }}
                  autoComplete="off"
                />
                {searching && <div className="sl-searching">搜尋中...</div>}
                {showSuggestions && memberSuggestions.length > 0 && (
                  <div className="sl-suggestions">
                    {memberSuggestions.map(m => (
                      <div key={m.id} className="sl-suggestion-item"
                        onMouseDown={e => { e.preventDefault(); handleSelectMember(m); }}>
                        <span className="sl-suggestion-name">{m.chi_name}</span>
                        {m.worship_id && <span className="sl-suggestion-id">{m.worship_id}</span>}
                        {m.mobile && <span className="sl-suggestion-mobile">{m.mobile}</span>}
                      </div>
                    ))}
                  </div>
                )}
                {showSuggestions && !searching && memberQuery.trim().length > 0 && memberSuggestions.length === 0 && (
                  <div className="sl-no-results">找不到相關會員</div>
                )}
              </div>
              {selectedMember && (
                <div className="sl-selected-member">
                  ✓ 已選：{selectedMember.chi_name}{selectedMember.worship_id ? ` · ${selectedMember.worship_id}` : ''}
                </div>
              )}
            </div>
            <div className="sl-form-group">
              <label className="sl-label">事奉崗位 <span className="sl-required">*</span></label>
              <select className="sl-select" value={positionId} onChange={e => setPositionId(e.target.value)}>
                <option value="">-- 請選擇崗位 --</option>
                {positions.map(p => <option key={p.id} value={p.id}>{p.position_name}</option>)}
              </select>
            </div>
            <div className="sl-form-group">
              <label className="sl-label">備註</label>
              <textarea className="sl-textarea" rows={3} placeholder="其他說明（可選）"
                value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
          </div>
          <div className="sl-modal-footer">
            <button type="button" className="sl-btn-cancel" onClick={onClose} disabled={saving}>取消</button>
            <button type="submit" className="sl-btn-submit" disabled={saving}>
              {saving ? '儲存中...' : '確認新增'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────
const ServantList = () => {
  const dispatch = useDispatch();
  const [viewMode, setViewMode] = useState('byMember'); // 'byPosition' | 'byMember'

  // Shared data
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [positionFilter, setPositionFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // byPosition state
  const [assignments, setAssignments] = useState([]);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });

  // byMember state
  const [servants, setServants] = useState([]);
  const [managingMember, setManagingMember] = useState(null);

  const batch = useExcelBatch({
    exportUrl: '/api/v1/serve-positions/assignments/export',
    templateUrl: '/api/v1/serve-positions/assignments/import-template',
    importUrl: '/api/v1/serve-positions/assignments/import',
    templateFilename: 'serve_assignment_import_template.xlsx',
    exportFilenamePrefix: 'serve_assignments_export',
  });

  useEffect(() => { loadPositions(); }, []);

  useEffect(() => {
    if (viewMode === 'byPosition') loadAssignments();
    else loadServants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, search, positionFilter, pagination.skip]);

  const loadPositions = async () => {
    try {
      const res = await servePositionService.getPositions({ limit: 1000, is_active: true });
      setPositions(res.items || []);
    } catch {}
  };

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const res = await servePositionService.getAssignments({
        skip: pagination.skip,
        limit: pagination.limit,
        search: search || undefined,
        position_id: positionFilter || undefined,
        status: 'active',
      });
      setAssignments(res.items || []);
      setPagination(prev => ({ ...prev, total: res.total }));
    } catch { dispatch(addNotification({ type: 'error', message: '載入事奉分配失敗' })); }
    finally { setLoading(false); }
  };

  const loadServants = async () => {
    setLoading(true);
    try {
      const res = await servePositionService.getAssignments({
        status: 'active',
        search: search || undefined,
        position_id: positionFilter || undefined,
        limit: 1000,
      });
      setServants(groupByMember(res.items || []));
    } catch { dispatch(addNotification({ type: 'error', message: '載入同工名單失敗' })); }
    finally { setLoading(false); }
  };

  const reload = () => {
    if (viewMode === 'byPosition') loadAssignments();
    else loadServants();
  };

  const handleDeleteAssignment = async (id) => {
    if (!window.confirm('確認移除此事奉分配？')) return;
    try {
      await servePositionService.deleteAssignment(id);
      dispatch(addNotification({ type: 'success', message: '已移除事奉分配' }));
      loadAssignments();
    } catch { dispatch(addNotification({ type: 'error', message: '移除失敗' })); }
  };

  const handleManageOpen = (member) => {
    setManagingMember({
      id: member.id,
      chi_name: member.chi_name,
      worship_id: member.worship_id,
      positions: member.positions.map(a => ({ id: a.id, position_id: a.position_id, status: a.status })),
    });
  };

  const switchView = (mode) => {
    setViewMode(mode);
    setSearch('');
    setPositionFilter('');
    setPagination(p => ({ ...p, skip: 0 }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="servant-list">
      {/* Top bar: view toggle + filters + add button */}
      <div className="sl-header">
        <div className="sl-view-toggle">
          <button
            className={`sl-view-btn ${viewMode === 'byMember' ? 'active' : ''}`}
            onClick={() => switchView('byMember')}
          >👤 按同工</button>
          <button
            className={`sl-view-btn ${viewMode === 'byPosition' ? 'active' : ''}`}
            onClick={() => switchView('byPosition')}
          >📋 按崗位</button>
        </div>
        <div className="sl-filters">
          <input
            type="text" className="search-input"
            placeholder="搜尋姓名或崇拜編碼..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}
          />
          <select className="filter-select" value={positionFilter}
            onChange={e => { setPositionFilter(e.target.value); setPagination(p => ({ ...p, skip: 0 })); }}>
            <option value="">所有崗位</option>
            {positions.map(p => <option key={p.id} value={p.id}>{p.position_name}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExcelBatchButtons batch={batch} />
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>+ 新增分配</button>
        </div>
      </div>

      <ExcelImportConfirmModal batch={batch} onConfirm={reload} />

      {/* Stats bar */}
      <div className="stats-bar">
        {viewMode === 'byMember'
          ? `共 ${servants.length} 位同工`
          : `共 ${pagination.total} 項事奉分配`}
      </div>

      {loading ? (
        <div className="sl-loading">載入中...</div>
      ) : viewMode === 'byMember' ? (
        /* ── 按同工 view ── */
        servants.length === 0 ? (
          <div className="sl-empty">暫無同工資料</div>
        ) : (
          <div className="table-container">
            <table className="servants-table">
              <thead>
                <tr>
                  <th>崇拜編碼</th>
                  <th>姓名</th>
                  <th>所屬小組</th>
                  <th>事奉崗位</th>
                  <th style={{ width: 90 }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {servants.map(member => (
                  <tr key={member.id}>
                    <td className="worship-id">{member.worship_id || '-'}</td>
                    <td className="name">{member.chi_name}</td>
                    <td>{member.groups || '-'}</td>
                    <td>
                      <div className="positions-list">
                        {member.positions.map(a => (
                          <span key={a.id} className="position-badge">{a.position_name}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <button className="btn-manage" onClick={() => handleManageOpen(member)}>
                        管理崗位
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        /* ── 按崗位 view ── */
        <div className="table-container">
          <table className="servants-table">
            <thead>
              <tr>
                <th>事奉崗位</th>
                <th>崇拜編碼</th>
                <th>姓名</th>
                <th>所屬小組</th>
                <th>手機</th>
                <th>備註</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {assignments.length === 0 ? (
                <tr><td colSpan={7} className="sl-empty">尚無事奉分配記錄</td></tr>
              ) : (
                assignments.map(a => (
                  <tr key={a.id}>
                    <td><span className="position-badge">{a.position_name}</span></td>
                    <td className="worship-id">{a.member_worship_id || '-'}</td>
                    <td className="name">{a.member_chi_name}</td>
                    <td>{a.member_groups || '-'}</td>
                    <td>{a.member_mobile || '-'}</td>
                    <td className="sl-notes">{a.notes || '-'}</td>
                    <td>
                      <button className="btn-delete" onClick={() => handleDeleteAssignment(a.id)}>
                        移除
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination (byPosition only) */}
      {viewMode === 'byPosition' && totalPages > 1 && (
        <div className="pagination">
          <button className="btn-secondary"
            onClick={() => setPagination(p => ({ ...p, skip: p.skip - p.limit }))}
            disabled={currentPage === 1}>上一頁</button>
          <span className="page-info">第 {currentPage} 頁，共 {totalPages} 頁</span>
          <button className="btn-secondary"
            onClick={() => setPagination(p => ({ ...p, skip: p.skip + p.limit }))}
            disabled={currentPage === totalPages}>下一頁</button>
        </div>
      )}

      {showAddModal && (
        <AddAssignmentModal
          positions={positions}
          onClose={() => setShowAddModal(false)}
          onSuccess={reload}
        />
      )}

      {managingMember && (
        <ManagePositionsModal
          member={managingMember}
          positions={positions}
          onClose={() => setManagingMember(null)}
          onSuccess={loadServants}
        />
      )}
    </div>
  );
};

export default ServantList;
